/**
 * GA4-14 spoke 3c — THE ADVERTISING TAG LAYER, WITH NO VENDOR SCRIPT IN IT.
 *
 * WHAT THIS SHIPS. One image request per advertisable page view, per vendor,
 * to a URL this file builds. Google Ads' documented remarketing pixel and
 * Meta's documented noscript pixel. No `gtag('config')` for an Ads
 * destination, no `fbevents.js`, no `fbq`, no vendor JavaScript of any kind.
 *
 * WHY IT STOPPED BEING A SCRIPT LAYER, and this is the whole of spoke 3c.
 * Spokes 3 and 3b hardened the script model twice and it leaked twice more,
 * each time through a vendor automation behind a switch neither our code nor
 * our CI can see:
 *
 *   1. `gtag('set', …)` is global, so a GA4 identity command reached the Ads
 *      destination — `uid=` on `ccm/collect`. Fixed by D10.
 *   2. Meta's automatic configuration instruments the DOM on `init` and sent
 *      `SubscribedButtonClick` carrying a typed search term, with the Events
 *      Manager toggle OFF. Fixed by D11 (`autoConfig`).
 *   3. Meta's pixel ALSO installs an HTML5 History State API listener by
 *      default and fires its own `PageView` on every `history.pushState` —
 *      Meta's own documentation, and a SEPARATE flag (`disablePushState`) from
 *      the one D11 sets. Verified in production 2026-09-01, after D11 and D12
 *      shipped: `ev=PageView` with `dl=…/messages`, `dl=…/?locations=Westlands`
 *      and `dl=…/auth`, all with `rl=` empty, on URLs this module's gate
 *      demonstrably refused — a direct load of `/?locations=` was silent.
 *      Google's side did the same class of thing through
 *      `en=form_start`/`form_submit` and a hashed email scraped from the /auth
 *      login form by account-side "user-provided data collection".
 *
 * The pattern is not a bug we keep missing. A vendor script observes the whole
 * document lifetime and carries account-configured behaviour that arrives over
 * the network after our code has finished running. Every fix we ship is a fix
 * for the automation we have already been burned by. So the decision (Brain,
 * 2026-09-01) is to stop: NO VENDOR SCRIPT RUNS ON THIS SITE. What leaves the
 * browser is one request, to one URL, chosen here, with the parameters written
 * out below and nothing else. Privacy Policy v3 §4.5 and §2.5 become true by
 * construction rather than by a gate we have to keep ahead of.
 *
 * WHAT THAT COSTS, stated rather than hidden. Google Ads' image pixel builds a
 * remarketing audience and does not report conversions; Meta's image pixel
 * records a PageView and supports no Advanced Matching, no Conversions API and
 * no attribution beyond it. That is the entire feature set this site was ever
 * going to use — the taxonomy has always been PageView and nothing else — but
 * a future conversion-tracking item cannot be built on this and will need its
 * own consent and disclosure review, exactly as it would have before.
 *
 * Neither vendor ID is written down here. Both come from the environment
 * (`VITE_GOOGLE_ADS_ID`, `VITE_META_PIXEL_ID`), and pages/__tests__/
 * consentMode.test.ts pins that no source file in the app contains the dataset
 * ID as a literal — so the only places it exists are Vercel's env and the
 * ticket. That is also what makes a build with no IDs a genuine no-op rather
 * than a switch somebody can flip by editing a constant.
 *
 * WHY IT IS STILL A SEPARATE MODULE. utils/gaGuard.ts owns `window.gtag` and
 * the GA4 property, and — since spoke 3c — nothing else: it configures gtag.js
 * for `GA_MEASUREMENT_ID` alone, exactly as it did after spoke 1. What it also
 * owns is the GATE, because the gate is a property of the visitor and the
 * route rather than of a vendor: `adPageViewAllowed` is asked from here.
 * This file owns the two vendors, and it is the only file in the app that
 * names either of their endpoints.
 *
 * The import direction is one-way and enforced by test:
 *   utils/consent.ts  ←  utils/gaGuard.ts  ←  utils/adTags.ts
 * consent.ts imports neither. gaGuard.ts does not import this file.
 *
 * ONE GATE, ONE DECISION, BOTH VENDORS. `adPageViewAllowed()` is asked once per
 * page view and both beacons are built from the same answer and the same URL,
 * so no consent, route or URL decision can apply to one vendor and not the
 * other. If the URL carries a query param the analytics sanitizer would have
 * stripped, the page view is SKIPPED for both rather than cleaned for either.
 *
 * THE ASYNC FIRE-TIME WINDOW IS GONE. Spoke 3b (D12) re-asked the conjunction
 * inside each vendor script's `load` callback, because the decision and the
 * send were separated by a network round trip and the vendor read
 * `document.location.href` for itself at the far end of it. There is no far
 * end any more. The decision and the request happen in one synchronous call
 * stack — the predicate is evaluated, the URL is built from it, and
 * `img.src = url` issues the request before the stack unwinds — so
 * `whenMetaScriptReady` and `whenGtagLoaderReady` have been deleted rather
 * than kept as no-ops. Nothing can move the address bar in between, because
 * the browser runs our handler to completion first.
 *
 * EVENT TAXONOMY: PageView, for both vendors, and nothing else. No sign_up, no
 * purchase, no generate_lead, no custom events, no Advanced Matching, no
 * Conversions API, no automatic events — the last of those now by construction,
 * since there is no vendor script to automate anything.
 *
 * WITHDRAWAL IS "STOP FIRING", AND THAT IS ALL IT NEEDS TO BE.
 *
 * The accepted limitation spoke 3 had to state — `fbevents.js` cannot be
 * unloaded, so a withdrawal could only silence it — does not exist here. There
 * is no script in memory to silence, no `fbq('consent','revoke')` to issue, and
 * no `_fbp`/`_fbc` to delete, because nothing on this site ever writes them: an
 * image request cannot set a first-party cookie with JavaScript, and the
 * vendor's own response cookies are third-party ones on the vendor's domain,
 * which are not ours to write or to remove. The gate reads consent from storage
 * on every call, so the instant advertising consent stops being granted the
 * next beacon does not happen. A visitor who withdraws mid-visit leaves nothing
 * of ours behind to clean up.
 */

import {
  ADS_ENABLED,
  GOOGLE_ADS_ID,
  adPageViewAllowed,
  onGaRouteChange,
  sanitizeAnalyticsUrl,
} from './gaGuard';
import { onConsentChange, type ConsentSnapshot } from './consent';

/**
 * The URL this document was OPENED with, captured while the bundle loads and
 * before anything hydrates.
 *
 * THE RACE THIS WINS, and it is the reason the two obvious implementations of
 * this module are both wrong. By the time an effect runs, the address bar may
 * already have been rewritten: Vike strips the query on hydration, so a landing
 * on `/?q=nairobi+2br` becomes `/`, and `<Navigate replace>` turns
 * `/join/PROMO50` into `/join`. Judging the landing page view on the live URL
 * therefore reads a URL the visitor never asked for — and, exactly backwards
 * from what is wanted, one that has had the disqualifying part removed. The
 * search-results view would be advertised on as if it were the bare home page.
 *
 * gaGuard captures `INITIAL_URL` for the mirror image of this problem (campaign
 * attribution lost to the same rewrite). Captured separately rather than
 * imported because the two want different things from it and coupling them
 * would make one file's fix the other's regression.
 */
const LANDING_URL = typeof window === 'undefined' ? '' : window.location.href;

/**
 * The Meta Pixel ID.
 *
 * Env-driven, and absent means absent: GA4-03's doctrine is that a build with
 * no ID measures nothing, and this module extends it to advertising. localhost,
 * every Vercel preview and every CI run fire no beacon — by design, not by
 * misconfiguration. Do not "fix" a preview that requests nothing.
 */
function resolveMetaPixelId(): string {
  const fromVite = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_META_PIXEL_ID) || '';
  const fromNode = (typeof process !== 'undefined' && process.env?.VITE_META_PIXEL_ID) || '';
  return String(fromVite || fromNode || '').trim();
}

export const META_PIXEL_ID = resolveMetaPixelId();

/**
 * Shape-checked rather than merely non-empty, like `GA_ENABLED` and
 * `ADS_ENABLED`. Meta dataset IDs are 15-16 digits; a placeholder, a truncated
 * paste or an APP id of a different length cannot half-enable the pixel.
 */
export const META_ENABLED = /^\d{15,16}$/.test(META_PIXEL_ID);

/**
 * GA4-14 spoke 3c (D14) — the NUMERIC half of `AW-18405568949`.
 *
 * Google's remarketing image tag addresses the conversion ID as a PATH SEGMENT,
 * and the path segment is the digits alone: the documented snippet is
 * `…/pagead/viewthroughconversion/<conversion id>/?…`. The `AW-` prefix is how
 * the ID is written in a `gtag('config')`, which this site no longer issues.
 *
 * Derived rather than stored, so the environment keeps carrying exactly one
 * value and the two spellings cannot drift. Empty when the env gate is shut,
 * which is what makes `googleAdsBeaconUrl` unreachable in that build.
 */
export const GOOGLE_ADS_CONVERSION_ID = ADS_ENABLED ? GOOGLE_ADS_ID.replace(/^AW-/, '') : '';

/**
 * GA4-14 spoke 3c (D14) — Google Ads' documented remarketing pixel.
 *
 * The shape is Google's own noscript remarketing tag, which is the only form of
 * this endpoint that is documented for a page to request directly:
 *
 *     <img src="//googleads.g.doubleclick.net/pagead/viewthroughconversion/
 *               <conversion id>/?value=0&guid=ON&script=0"/>
 *
 * and this file sends exactly that, plus `url=`.
 *
 * `value=0` IS SENT, and the parameter order above is Google's own. Spoke 3c
 * first shipped without it, on the reasoning that a conversion-value field has
 * no meaning on a remarketing ping with no conversion attached. Brain ruled
 * against that on 2026-09-01: match the printed snippet exactly, because the
 * snippet is the shape Google's own systems are documented to receive and a
 * deviation we invented is a deviation we would have to defend. It is a
 * constant zero and describes nothing about the visitor.
 *
 * ONE DELIBERATE ADDITION TO THAT SNIPPET, reported rather than made quietly:
 * `url=`, carrying the sanitized page URL. Google documents it as the parameter
 * that names the page for rule-based audience lists (Tag Assistant, "Google Ads
 * remarketing errors"), and it is load-bearing here for a reason the snippet
 * does not have to think about: with `referrerPolicy` set to
 * `strict-origin-when-cross-origin` the `Referer` header is the bare origin, so
 * this parameter is the ONLY thing that tells Google which page this was — and
 * the only place that value is chosen by us rather than read off the address
 * bar by somebody else's script.
 *
 * Nothing else. No `label` (that is conversion tracking), no `data`, no
 * `random`, no cache-buster: a parameter that is not here cannot describe a
 * visitor, and pages/__tests__/consentModeNetwork.test.ts pins the key set
 * exactly rather than as a subset.
 */
export const GOOGLE_ADS_BEACON_ENDPOINT =
  'https://googleads.g.doubleclick.net/pagead/viewthroughconversion';

/**
 * GA4-14 spoke 3c (D15) — Meta's documented noscript pixel.
 *
 * Meta's own base code prints this as the `<noscript>` fallback:
 *
 *     <img src="https://www.facebook.com/tr?id=<pixel id>&ev=PageView&noscript=1"/>
 *
 * TWO ADDITIONS, both reported rather than assumed:
 *
 *   - `dl=` carries the sanitized page URL, for the same reason `url=` does on
 *     the Google side. The JavaScript pixel sends `dl` from
 *     `document.location.href` on every request it makes; sending it ourselves
 *     is what makes the value OURS. Without it Meta falls back to the `Referer`
 *     header, which our referrer policy has already reduced to the origin — so
 *     the choice is not "clean URL or no URL", it is "our URL or none".
 *   - `dpo=LDU&dpoco=0&dpost=0` is Limited Data Use, the query-parameter form
 *     of `fbq('dataProcessingOptions', ['LDU'], 0, 0)` that spoke 3 issued on
 *     the script. Same meaning: LDU on, and the two zeroes leave country and
 *     state for Meta to geolocate. It is CCPA/CPRA's mechanism for a visitor in
 *     a US state that treats this sharing as a "sale", and dropping it because
 *     the script that used to carry it is gone would be a real regression.
 *
 * No `rl` (a referrer we are deliberately not sending), no `ud[…]` (Advanced
 * Matching, which this site does not do and the policy says it does not do), no
 * `cd[…]` custom data, no `eid`. The key set is pinned exactly.
 */
export const META_BEACON_ENDPOINT = 'https://www.facebook.com/tr';

/**
 * The path an ad page view was last actually SENT for.
 *
 * The exact counterpart of gaGuard's `lastSentPath`, and it exists for two
 * reasons that are easy to miss:
 *
 *   1. `onGaRouteChange` fires on same-path `replaceState`. pages/Home.tsx
 *      rewrites `/` with the filter state on EVERY filter change, so without
 *      this a visitor ticking four filter chips would send four beacons for one
 *      screen.
 *   2. It is what lets a consent grant make good the page the visitor is
 *      standing on without re-counting a screen that was already counted.
 *
 * Recorded on FIRE, never on consider — a path that was refused by the gate has
 * not been advertised on and must still be eligible when the gate opens. Spoke
 * 3b needed a second marker for a page view that had been decided but not yet
 * sent; there is no such state any more, so `pendingAdPath` is gone with it.
 */
let lastAdFiredPath: string | null = null;

/**
 * GA4-14 spoke 3b (D12) — THE URL THE CURRENT VIEW IS JUDGED FOR.
 *
 * Starts as the URL the document was opened with, and becomes the live URL at
 * each real navigation. A `replace` NEVER updates it, because a replace is the
 * app correcting the address bar for the screen already on show — so the two
 * rewrites this module exists to survive cannot launder a refusal into a page
 * view:
 *
 *   - `/?q=SECRETTERM` → Vike replaces with `/`. Same screen, still the search
 *     results, still refused. Judged on the clean `/` the visitor's search
 *     would have chosen the audience they entered.
 *   - `/join/PROMO50` → redirected with `<Navigate replace>` to `/join`. The
 *     landing was a campaign code, which is a dynamic segment and out of the
 *     allowlist by construction; the redirect does not make it a fresh visit.
 *
 * KEPT FOR VIKE HYDRATION under spoke 3c, and still load-bearing even though
 * the fire is synchronous now: the rewrite happens between the document opening
 * and `+Layout` mounting, so the very first decision this module makes is
 * already looking at a URL the visitor did not ask for.
 *
 * A real navigation afterwards — a click, a back button — is a new screen and
 * is judged on its own merits.
 */
let openedUrl = LANDING_URL;

/**
 * GA4-14 spoke 3c (D14) — Google Ads' beacon URL for one page view.
 *
 * Exported so the tests can assert the shape against the documentation rather
 * than against a regex copied out of the implementation.
 */
export function googleAdsBeaconUrl(sanitizedUrl: string): string {
  return (
    `${GOOGLE_ADS_BEACON_ENDPOINT}/${GOOGLE_ADS_CONVERSION_ID}/` +
    `?value=0&guid=ON&script=0&url=${encodeURIComponent(sanitizedUrl)}`
  );
}

/** GA4-14 spoke 3c (D15) — Meta's beacon URL for one page view. */
export function metaBeaconUrl(sanitizedUrl: string): string {
  return (
    `${META_BEACON_ENDPOINT}?id=${META_PIXEL_ID}&ev=PageView&noscript=1` +
    `&dl=${encodeURIComponent(sanitizedUrl)}&dpo=LDU&dpoco=0&dpost=0`
  );
}

/**
 * GA4-14 spoke 3c (D17) — THE ONLY THING IN THIS APP THAT CONTACTS AN AD VENDOR.
 *
 * An `Image` that is never inserted into the document. Setting `src` is what
 * issues the request; the element is then unreferenced and collected, and
 * nothing about it is in the DOM for a mutation observer, a CSP report or a
 * later reader to find. There is no `onload`, because whether the vendor
 * answered is not a fact this app has any use for.
 *
 * `referrerPolicy` is set BEFORE `src`, and the order matters: the policy has
 * to be in place when the request is created or the browser uses the document
 * default, which on this site would send the full page URL as `Referer` to both
 * vendors. `strict-origin-when-cross-origin` reduces that to
 * `https://www.afriqahome.com/` for a cross-origin request — so the only URL
 * either vendor receives is the sanitized one this file put in the query
 * string, and the fragment and query of the live address bar never leave.
 *
 * Guarded on `Image` existing rather than on `window`, because the SSR runtime
 * and the bare-`tsx` test runner have a `window` shim without one and must not
 * throw here.
 */
function fireBeacon(url: string): void {
  if (typeof Image === 'undefined') return;
  const img = new Image();
  img.referrerPolicy = 'strict-origin-when-cross-origin';
  img.src = url;
}

/**
 * One ad page view: Google Ads and Meta, together or not at all.
 *
 * SYNCHRONOUS, END TO END, and that is D17's whole point. The predicate is
 * evaluated, the URL is built from the answer, and both requests are issued
 * before this function returns — one call stack, with no timer, no promise and
 * no vendor `load` event anywhere in it. The window spoke 3b had to re-check
 * across does not exist, so there is nothing to re-check.
 *
 * Returns true when the vendors were actually contacted. The caller does not
 * need that — it is there so the tests can assert on the decision rather than
 * only on its side effects.
 */
function sendAdPageViews(): boolean {
  if (typeof window === 'undefined') return false;

  const path = window.location.pathname;

  // A screen is advertised on once. `lastAdFiredPath` is written on FIRE, so
  // neither a same-path `replaceState` (which pages/Home.tsx emits on every
  // filter change) nor a consent make-good can count one screen twice.
  if (path === lastAdFiredPath) return false;

  // GA4-14 spoke 3b (D12) — THE FULL CONJUNCTION, on both URLs.
  //
  // `openedUrl` is the URL this view is judged for: the one the document was
  // opened with while the app is still settling, and the live URL at the last
  // real navigation afterwards. `adPageViewAllowed` asks the route allowlist
  // and the query rule of BOTH it and `window.location`, plus consent, GPC,
  // staff and native — each read fresh, none cached.
  if (!adPageViewAllowed(openedUrl)) return false;

  // GA4-14 spoke 3c (D17) — THE URL THE VENDORS ARE HANDED.
  //
  // The gate above has already established that the sanitizer would not change
  // this URL — that is what `urlIsAdSafe` means, and it is why an ineligible
  // query SKIPS the view rather than cleaning it. So this is the raw eligible
  // URL. It goes through the sanitizer anyway, here, at the point the vendor's
  // parameter is BUILT: the invariant "no ad vendor is handed a URL the
  // sanitizer would have touched" is then enforced where the URL is written
  // rather than asserted three functions away.
  const sanitizedUrl = sanitizeAnalyticsUrl(window.location.href);
  if (!sanitizedUrl) return false;

  // NOT GATED ON EACH OTHER, and that is deliberate rather than an oversight:
  // the only thing that can refuse one vendor and not the other is the ENV, and
  // "no ID means no vendor" is decided per vendor — a build with
  // `VITE_META_PIXEL_ID` and no `VITE_GOOGLE_ADS_ID` runs the Meta half alone.
  // Every consent, route and URL decision is `adPageViewAllowed` above, which
  // both passed, so the two can never disagree about whether a screen is
  // advertisable or about which URL it was.
  let fired = false;
  if (ADS_ENABLED) {
    fireBeacon(googleAdsBeaconUrl(sanitizedUrl));
    fired = true;
  }
  if (META_ENABLED) {
    fireBeacon(metaBeaconUrl(sanitizedUrl));
    fired = true;
  }

  // Neither vendor is configured in this build. Nothing was sent, so nothing
  // may be recorded — a later grant on this same screen must still be able to
  // fire it.
  if (!fired) return false;

  lastAdFiredPath = path;
  return true;
}

/**
 * What an advertising decision DOES, as opposed to what it stores.
 *
 * The counterpart of gaGuard's `applyConsentChange`, for the vendors gaGuard
 * does not own. Registered at module scope for the same reason that one is: a
 * decision must be acted on whether or not a component happened to be mounted.
 *
 * WITHDRAWAL IS A NO-OP HERE, and that is the shape of spoke 3c rather than an
 * omission. There is no script to silence, no vendor consent verb to issue and
 * no `_fbp`/`_fbc` of ours to delete — an image request cannot write a
 * first-party cookie, and this site no longer runs anything that could. The
 * gate reads consent from storage on every call, so "stop firing" is complete
 * and immediate the moment the record changes.
 *
 * RE-GRANT makes good the screen the visitor is standing on, subject to the
 * same "a screen is counted once" rule as the analytics make-good.
 *
 * CROSS-TAB IS FREE: utils/consent.ts's `storage` listener delivers the other
 * tab's decision here as an ordinary consent notification, and because the gate
 * re-reads storage rather than trusting this snapshot, a withdrawal in another
 * tab stops the next beacon in this one even if the notification were lost.
 */
function applyAdConsentChange(snapshot: ConsentSnapshot): void {
  if (typeof window === 'undefined') return;
  if (snapshot.advertising !== 'granted') return;
  sendAdPageViews();
}

/**
 * Registered at module scope, exactly as gaGuard's consent subscriber is.
 *
 * Safe with no DOM: `onConsentChange` touches `window` only through a guarded
 * `storage` listener, and `applyAdConsentChange` returns immediately without
 * one.
 */
onConsentChange(applyAdConsentChange);

/**
 * Start advertising page views: one for the page this document opened with,
 * then one per client-side route change.
 *
 * IT ADDS NO ROUTER LISTENER. gaGuard's `startGaPageviewTracking` already owns
 * the single patch on `history.pushState`/`replaceState`, and two modules
 * wrapping the same method cannot be unwound in an arbitrary order without
 * leaving one installed forever. This subscribes to the signal that patch
 * publishes — `onGaRouteChange` — and does its own dedupe, because that signal
 * fires for same-path rewrites too.
 *
 * The signal is delivered SYNCHRONOUSLY from inside the patched history method
 * (see `notifyGaRouteChange` in utils/gaGuard.ts), after the navigation has
 * been applied, so `window.location` is already the destination here and the
 * beacon leaves in the same task as the navigation that earned it.
 *
 * The initial view is sent here rather than waiting for a navigation, because
 * `onGaRouteChange` does not fire for the page the document opened with.
 *
 * Called from src/pages/+Layout.tsx, which is the Vike tree: the native shell
 * mounts App.tsx directly and never reaches it. The runtime `isNativePlatform`
 * check inside `canFireAdTags` is the belt to that braces, and it is the one
 * that matters — capacitor.config.ts allows the app's webview onto
 * www.afriqahome.com, where this code really does run.
 *
 * Returns a cleanup function, matching `startGaPageviewTracking`'s shape.
 */
export function startAdTagTracking(): () => void {
  if (typeof window === 'undefined') return () => {};

  const unsubscribe = onGaRouteChange((kind) => {
    // A push or a pop is a navigation; a replace is the app tidying the URL of
    // the screen already on show. Only the former makes a new URL the one
    // subsequent views are judged for — see `openedUrl`. The history patch in
    // utils/gaGuard.ts applies the navigation BEFORE it notifies, so
    // `window.location` is already the destination here.
    if (kind === 'push' || kind === 'pop') openedUrl = window.location.href;
    sendAdPageViews();
  });

  sendAdPageViews();

  return unsubscribe;
}
